import { createRoot, Root } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Keep federation-injected MFE CSS <link> tags ABOVE the shell's stylesheet
// in <head>, so shell utility classes cascade last and win.
//
// Why this is needed:
// vite-plugin-federation's remoteEntry.js eagerly calls dynamicLoadingCss()
// each time an MFE expose is first imported (see
// apps/<mfe>/dist/assets/remoteEntry.js). That helper synchronously
// document.head.appendChild()s the MFE's bundled CSS file as a <link>. The
// link lands AFTER the shell's own <link rel="stylesheet">, and at equal
// specificity the last source-order rule wins the cascade. The MFE's Tailwind
// build defines the same base utilities as the shell — including `.hidden
// { display: none }` and the responsive `md:flex` rule — so once the MFE
// link is in the head, the MFE's `.hidden` rule beats the shell's `md:flex`
// rule for `<nav class="hidden md:flex">…Home / Dashboard…</nav>` in the
// shell Header. Result: breadcrumb, financial-year period selector, and the
// desktop sidebar collapse button all disappear on every soft navigation
// into any MFE. Hard refresh appears to "fix" it only because React paints
// the shell header BEFORE federation has finished appending the MFE link;
// the bug returns the moment the MFE link drops in.
//
// Each MFE's routes.tsx also injects an inline <style data-mfe="..."> BEFORE
// the shell link via `import './index.css?inline'`. That part still helps
// (it makes MFE-only classes available before the federation link finishes
// downloading) but it does NOT solve the cascade — federation's appended
// link still wins. We solve it here by tagging the federation link with
// data-mfe and moving it ABOVE the first non-MFE stylesheet. After this,
// the order in <head> is always: [MFE styles…, shell stylesheet], and the
// shell wins. Idempotent and safe for cross-MFE navigation: each new link
// added by federation triggers the observer, gets tagged, and slides above
// the shell.
(() => {
  if (typeof document === "undefined") return;

  // A federation-injected MFE stylesheet is always named `style-<hash>.css`
  // under an `/assets/` path — but so is the shell's OWN entry stylesheet
  // (both are vite builds, e.g. shell ships /assets/style-V346VTvo.css). So we
  // can't identify an MFE link by filename alone; we'd wrongly tag and hoist
  // the shell's own sheet. Two signals reliably tell them apart:
  //   1. Prod (Caddy): the MFE link is path-prefixed `/mfe/<name>/assets/...`
  //      while the shell link is bare `/assets/...`.
  //   2. Dev / static-serve (serve-production.ps1): remotes are configured
  //      with ABSOLUTE origin URLs in vite.config.ts
  //      (e.g. http://localhost:8086/assets/remoteEntry.js), so federation's
  //      dynamicLoadingCss appends the MFE link CROSS-ORIGIN to the shell,
  //      whereas the shell's own stylesheet is same-origin.
  // The original regex matched ONLY signal 1, so it silently missed signal 2
  // and left the MFE link below the shell sheet — which is exactly why the
  // header's `hidden md:flex` controls (period selector, sidebar toggle, etc.)
  // vanished on every soft navigation into an MFE until a hard refresh.
  const STYLE_FILE_RE = /\/assets\/style-[^/]+\.css/;
  const MFE_PREFIX_RE = /\/mfe\/[^/]+\/assets\/style-[^/]+\.css/;

  const isMfeLink = (href: string): boolean => {
    if (!STYLE_FILE_RE.test(href)) return false;
    if (MFE_PREFIX_RE.test(href)) return true; // prod: /mfe/<name>/ proxy path
    try {
      // dev / static-serve: MFE bundles load from a different origin than the
      // shell. The shell's own stylesheet is same-origin, so this excludes it.
      return new URL(href, document.baseURI).origin !== window.location.origin;
    } catch {
      return false;
    }
  };

  const reorderMfeLink = (link: HTMLLinkElement) => {
    if (link.hasAttribute("data-mfe")) return;
    if (!isMfeLink(link.href || "")) return;
    link.setAttribute("data-mfe", "federation");
    const firstShellSheet = Array.from(
      document.head.querySelectorAll('link[rel="stylesheet"], style'),
    ).find((el) => !el.hasAttribute("data-mfe") && el !== link);
    if (firstShellSheet && firstShellSheet !== link.nextSibling) {
      document.head.insertBefore(link, firstShellSheet);
    }
  };

  // Sweep anything already in <head> (e.g. if federation ran before this
  // IIFE for some reason).
  document.head
    .querySelectorAll('link[rel="stylesheet"]')
    .forEach((l) => reorderMfeLink(l as HTMLLinkElement));

  // Watch for future appends from federation's dynamicLoadingCss.
  new MutationObserver((muts) => {
    for (const m of muts) {
      m.addedNodes.forEach((node) => {
        if (node instanceof HTMLLinkElement && node.rel === "stylesheet") {
          reorderMfeLink(node);
        }
      });
    }
  }).observe(document.head, { childList: true });
})();

// Global 401 guard — catches unauthorized responses from ALL MFE axios instances,
// not just the shell's own. When any fetch/XHR returns 401 while on a
// protected route, clear the token and redirect to /login immediately.
//
// Scoped to same-origin requests only. Every ESG MFE calls its own backends
// through the shell's same-origin `/api` proxy (see defaultShellApiBaseUrl in
// apiClient.ts), so same-origin is the correct signal for "this 401 is about
// MY session". Embedded third-party widgets (e.g. the Brew chat widget in
// apps/brew-mfe) call their OWN cross-origin backend (VITE_BREW_BASE_URL,
// e.g. assist.beta.breww.ai) with their OWN separate auth/session — a 401
// from that call means the widget's own session is unlinked, NOT that the
// ESG login is invalid. Without this scoping, any such unrelated cross-origin
// 401 force-logged-out the whole ESG session a few seconds after every login.
(() => {
  const PUBLIC_PATHS = [
    '/login',
    '/login/verify-otp',
    '/accept-invitation',
    '/forgot-password',
    '/reset-password',
    '/survey/',
    '/cbam-supplier/',
  ];
  const isPublicPath = () =>
    PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

  const isSameOriginUrl = (url: string): boolean => {
    try {
      return new URL(url, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  };

  const forceLogout = () => {
    if (isPublicPath()) return;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  // A 401 should only force logout when the request actually sent a Bearer
  // token AND the client still has one stored. A 401 on a request that went
  // out with no Authorization header is nearly always a race (parallel
  // requests firing while localStorage.token is being rewritten right after
  // login or an MFE switch). Force-logging out on those was the source of
  // the intermittent auto-logout when navigating Profile / Org Chart.
  const sentBearer = (h: HeadersInit | undefined | null): boolean => {
    if (!h) return false;
    if (h instanceof Headers) {
      const v = h.get('Authorization');
      return !!v && v.startsWith('Bearer ');
    }
    if (Array.isArray(h)) {
      return h.some(
        ([k, v]) =>
          typeof k === 'string' &&
          k.toLowerCase() === 'authorization' &&
          typeof v === 'string' &&
          v.startsWith('Bearer '),
      );
    }
    // Plain object
    const rec = h as Record<string, string>;
    const key = Object.keys(rec).find((k) => k.toLowerCase() === 'authorization');
    return !!key && typeof rec[key] === 'string' && rec[key].startsWith('Bearer ');
  };

  const shouldForceLogoutOn401 = (sentAuth: boolean): boolean =>
    sentAuth && !!localStorage.getItem('token');

  // Patch window.fetch
  const _fetch = window.fetch.bind(window);
  (window as any).fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ) => {
    const res = await _fetch(input, init);
    if (res.status === 401) {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : (input as Request).url;
      // Pull headers from init first (raw fetch), then fall back to Request
      let headers: HeadersInit | undefined = init?.headers;
      if (!headers && input instanceof Request) headers = input.headers;
      const sentAuth = sentBearer(headers);
      if (isSameOriginUrl(url) && shouldForceLogoutOn401(sentAuth)) {
        forceLogout();
      }
    }
    return res;
  };

  // Patch XMLHttpRequest (axios uses XHR under the hood)
  const _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    (this as unknown as { __reqUrl: string }).__reqUrl =
      typeof url === 'string' ? url : url.href;
    (this as unknown as { __sentAuth: boolean }).__sentAuth = false;
    return (_open as any).call(this, method, url, ...rest);
  };
  const _setRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
  XMLHttpRequest.prototype.setRequestHeader = function (
    this: XMLHttpRequest,
    name: string,
    value: string,
  ) {
    if (
      typeof name === 'string' &&
      name.toLowerCase() === 'authorization' &&
      typeof value === 'string' &&
      value.startsWith('Bearer ')
    ) {
      (this as unknown as { __sentAuth: boolean }).__sentAuth = true;
    }
    return (_setRequestHeader as any).call(this, name, value);
  };
  const _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (...args: any[]) {
    this.addEventListener('load', function (this: XMLHttpRequest) {
      const url = (this as unknown as { __reqUrl?: string }).__reqUrl || '';
      const sentAuth =
        (this as unknown as { __sentAuth?: boolean }).__sentAuth === true;
      if (
        this.status === 401 &&
        isSameOriginUrl(url) &&
        shouldForceLogoutOn401(sentAuth)
      ) {
        forceLogout();
      }
    });
    return _send.apply(this, args as any);
  };
})();

// Right-click is switched off on the deployed production host only.
//
// A deterrent, not a protection: the browser menu, the keyboard and a direct
// API call all still reach everything this page can. What protects the data is
// the permission check on every request, on the server.
//
// Decided by the host the page is served from, not by the build mode: local
// "production mode" (serve-production.ps1) is a PROD build served from
// localhost, and inspect must stay available there. Anything on localhost,
// 127.0.0.1, *.localhost or a private LAN address is treated as not
// production.
//
// Registered once here, on the document, so it covers every MFE the shell
// loads without each of them carrying a copy. Only the browser's own menu is
// suppressed — the event still travels, so a component that draws its own
// context menu keeps working.
//
// Left alone inside anything the user types into: the menu there is how people
// paste, and how the spell checker offers its corrections.
//
// Set VITE_DISABLE_RIGHT_CLICK=false to turn this off for a build, or
// VITE_DISABLE_RIGHT_CLICK=true to force it on regardless of host.
(() => {
  if (typeof document === "undefined") return;
  if (!import.meta.env.PROD) return;
  const flag = import.meta.env.VITE_DISABLE_RIGHT_CLICK;
  if (flag === "false") return;

  const host = window.location.hostname.toLowerCase();
  const isLocalHost =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host === "[::1]" ||
    host.endsWith(".localhost") ||
    /^10\.\d+\.\d+\.\d+$/.test(host) ||
    /^192\.168\.\d+\.\d+$/.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(host);
  if (isLocalHost && flag !== "true") return;

  document.addEventListener(
    "contextmenu",
    (event) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.closest?.(
        'input, textarea, select, [contenteditable=""], [contenteditable="true"]',
      );
      if (typing) return;
      event.preventDefault();
    },
    { capture: true },
  );
})();

const container = document.getElementById("root")!;
const root: Root = createRoot(container);
root.render(<App />);
